'use client';

import { useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import {
  BookOpen,
  Clock,
  Film,
  Gem,
  Gift,
  Heart,
  HelpCircle,
  Home,
  Instagram,
  Languages,
  LayoutGrid,
  Mail,
  MessageCircle,
  MoreVertical,
  SquarePen,
  User,
  SquareEqual,
  Wand2,
} from 'lucide-react';

import { useAuthModal } from '~/components/auth/auth-modal-context';

import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_OPEN,
  useSidebar,
} from './sidebar-context';

interface NavItem {
  Icon: React.ElementType;
  label: string;
  path: string;
  external?: boolean;
  /** Gold "upgrade" treatment. */
  gold?: boolean;
  /** Purple-tinted icon (create character / scenario). */
  accent?: boolean;
}

const mainNav: NavItem[] = [
  { Icon: Home, label: 'Home', path: '/' },
  { Icon: BookOpen, label: 'Catalog', path: '/catalog' },
  { Icon: SquareEqual, label: 'Feed', path: '/feed' },
  { Icon: MessageCircle, label: 'Chat', path: '/chat' },
  { Icon: Wand2, label: 'Create Character', path: '/create' },
  { Icon: Film, label: 'Create Scenario', path: '/generate' },
  { Icon: Heart, label: 'Companions', path: '/my-ai' },
  { Icon: Clock, label: 'Daily Challenges', path: '/challenges' },
  { Icon: Gem, label: 'Upgrade', path: '/pricing', gold: true },
];

const footerNav: NavItem[] = [
  { Icon: Mail, label: 'Contact Us', path: '/contact' },
  { Icon: SquarePen, label: 'Blog', path: '/blog' },
  {
    Icon: Instagram,
    label: 'Instagram',
    path: 'https://www.instagram.com/edemaiofficial/',
    external: true,
  },
];

// Revealed as a floating overlay when the "More" button is pressed.
const moreNav: NavItem[] = [
  { Icon: User, label: 'Profile', path: '/account' },
  { Icon: Languages, label: 'Languages', path: '/account/language' },
  { Icon: Gift, label: 'Affiliate', path: '/affiliate' },
  { Icon: HelpCircle, label: 'FAQ', path: '/faq' },
];

const SIDEBAR_CSS = `
.edem-sb { background: #0c0c0f; }
.edem-brand { font-family: Georgia, 'Times New Roman', serif; }
.edem-brand .e { color: #ffffff; }
.edem-brand .ai { color: #b07cf0; }
.edem-ham { color: #e9e9f0; border-radius: 9px; padding: 7px; transition: background .2s; }
.edem-ham:hover { background: rgba(255,255,255,0.08); }

.edem-pill {
  display: flex; align-items: center; gap: 11px;
  padding: clamp(9px, 1.3vh, 14px) 12px; border-radius: 13px;
  border: 1px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.015);
  color: #eaeaf1; font-size: clamp(13px, 1.55vh, 15px); font-weight: 600;
  transition: border-color .2s, background .2s, box-shadow .2s, padding-left .3s cubic-bezier(0.4,0,0.2,1), padding-right .3s cubic-bezier(0.4,0,0.2,1);
}
.edem-pill:hover { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); }
.edem-pill svg { width: clamp(16px, 1.9vh, 19px); height: clamp(16px, 1.9vh, 19px); flex-shrink: 0; color: #cdcdd7; }
.edem-navlist { display: flex; flex-direction: column; gap: clamp(6px, 0.95vh, 12px); }
.edem-bottom { margin-top: auto; }
.edem-pill svg.edem-acc { color: #b07cf0; }
.edem-pill .edem-lbl { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.edem-pill--icon { justify-content: center; padding-left: 0; padding-right: 0; }

.edem-pill--active {
  border-color: rgba(168,85,247,0.6);
  background: rgba(168,85,247,0.10);
  box-shadow: 0 0 18px rgba(168,85,247,0.30), inset 0 0 16px rgba(168,85,247,0.08);
  color: #ffffff;
}
.edem-pill--active svg { color: #c084fc; }
.edem-pill--active:hover { border-color: rgba(168,85,247,0.75); background: rgba(168,85,247,0.14); }

.edem-pill--gold {
  border-color: rgba(234,179,8,0.6);
  background: linear-gradient(90deg, rgba(234,179,8,0.15), rgba(234,179,8,0.04));
  box-shadow: 0 0 18px rgba(234,179,8,0.26), inset 0 0 16px rgba(234,179,8,0.07);
  color: #fcd34d;
}
.edem-pill--gold svg { color: #fbbf24; }
.edem-pill--gold:hover { border-color: rgba(234,179,8,0.85); }

.edem-divider { height: 1px; background: rgba(255,255,255,0.1); margin: clamp(22px, 2.8vh, 38px) 6px clamp(10px, 1.3vh, 16px); }

/* "More" reveal — a floating overlay anchored just above the button. It slides UP
   over the top part of the sidebar and does NOT push or scroll the layout. */
.edem-more-btn { width: 100%; cursor: pointer; text-align: left; font: inherit; }
.edem-more-btn--on { border-color: rgba(255,255,255,0.18); background: rgba(255,255,255,0.06); color: #ffffff; }
.edem-more-btn--on svg { color: #ffffff; }
.edem-more-anchor { position: relative; }
.edem-more-pop {
  position: absolute; left: 0; right: 0; bottom: calc(100% + clamp(6px, 0.95vh, 12px));
  display: flex; flex-direction: column; gap: clamp(6px, 0.95vh, 12px);
  padding: 8px; border-radius: 14px;
  background: #121216; border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 -14px 40px rgba(0,0,0,0.6);
  max-height: 70vh; overflow-y: auto;
  transform: translateY(10px); transform-origin: bottom center;
  opacity: 0; pointer-events: none;
  transition: transform .26s cubic-bezier(0.34,1.1,0.6,1), opacity .2s ease;
  z-index: 60;
}
.edem-more-pop.open { transform: translateY(0); opacity: 1; pointer-events: auto; }
`;

export function Sidebar() {
  const pathname = usePathname();
  const { isOpen, mobileOpen, setMobileOpen, isAnimating } = useSidebar();
  const { isOpen: isAuthModalOpen } = useAuthModal();
  const [moreOpen, setMoreOpen] = useState(false);

  // On desktop the sidebar follows isOpen (expand/collapse). The mobile drawer
  // always renders expanded (labels shown) whenever it slides in.
  const expanded = mobileOpen || isOpen;
  const asideWidth = expanded ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED;

  const handleNavClick = () => {
    // Close the drawer after navigating (no-op on desktop, where it's closed).
    setMobileOpen(false);
  };

  // Only the MOST SPECIFIC matching link is active. With nested paths like
  // /account and /account/language, a plain startsWith highlights both, so we
  // pick the longest matching path across every nav item and highlight just that.
  const activePath = [...mainNav, ...footerNav, ...moreNav]
    .filter((i) => !i.external && i.path !== '#')
    .filter((i) =>
      i.path === '/'
        ? pathname === '/'
        : pathname === i.path || pathname.startsWith(`${i.path}/`),
    )
    .reduce<string | null>(
      (best, i) => (best && best.length >= i.path.length ? best : i.path),
      null,
    );

  const renderPill = (item: NavItem) => {
    // Active only when this item is the single most-specific match (see activePath).
    const isActive =
      !item.external && item.path !== '#' && item.path === activePath;
    const className = [
      'edem-pill',
      isActive && !item.gold ? 'edem-pill--active' : '',
      item.gold ? 'edem-pill--gold' : '',
      !expanded ? 'edem-pill--icon' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const inner = (
      <>
        <item.Icon className={item.accent ? 'edem-acc' : undefined} />
        {expanded && <span className="edem-lbl">{item.label}</span>}
      </>
    );

    if (item.external) {
      return (
        <a
          key={item.label}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          title={!expanded ? item.label : undefined}
        >
          {inner}
        </a>
      );
    }
    return (
      <Link
        key={item.label}
        href={item.path}
        onClick={handleNavClick}
        className={className}
        title={!expanded ? item.label : undefined}
      >
        {inner}
      </Link>
    );
  };

  return (
    <>
      <style>{SIDEBAR_CSS}</style>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[105] bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`edem-sb fixed left-0 ${isAuthModalOpen ? '' : 'border-r border-white/10'} z-[110] ${
          isAnimating ? 'transition-all duration-300 ease-in-out' : ''
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        style={{
          width: `${asideWidth}px`,
          // Below the 64px navbar, plus the promo banner when it's visible
          top: 'calc(64px + var(--promo-banner-h, 0px))',
          // dvh (not vh): on iPhones 100vh is the MAXIMIZED viewport, so
          // with Safari's URL bar expanded the sidebar tail hid behind it.
          // dvh tracks the real visible height; the skirt below masks the
          // bar-collapse transition lag.
          height: 'calc(100dvh - 64px - var(--promo-banner-h, 0px))',
        }}
      >
        {/* Skirt: iOS Safari's collapsing URL bar makes fixed elements lag
            while scrolling, flashing page background below — fill it. */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            height: 140,
            background: '#0c0c0f',
          }}
        />
        {/* Auth modal dimmer overlay */}
        <div
          className={`pointer-events-none absolute inset-0 z-[5] bg-black/50 transition-all duration-300 ${
            isAuthModalOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <div className="flex h-full flex-col">
          {/* Main nav scrolls on its own. Collapsed mode uses a smaller inset. */}
          <nav
            className="flex-1 overflow-y-auto pt-4"
            style={{
              paddingLeft: expanded ? '24px' : '8px',
              paddingRight: expanded ? '24px' : '8px',
              transition: isAnimating
                ? 'padding 300ms cubic-bezier(0.4, 0, 0.2, 1)'
                : undefined,
            }}
          >
            <div className="edem-navlist">{mainNav.map(renderPill)}</div>
          </nav>

          {/* Footer pinned at the bottom. "More" opens its links as a floating
              overlay ABOVE the button — over the top part of the sidebar — with no
              layout shift and no scrolling. */}
          <div
            className="shrink-0 pb-4"
            style={{
              paddingLeft: expanded ? '24px' : '8px',
              paddingRight: expanded ? '24px' : '8px',
              transition: isAnimating
                ? 'padding 300ms cubic-bezier(0.4, 0, 0.2, 1)'
                : undefined,
            }}
          >
            <div className="edem-divider" />

            <div className="edem-navlist">
              {footerNav.map(renderPill)}

              <div className="edem-more-anchor">
                {/* Floating overlay — sits above the button, over the content. */}
                <div className={`edem-more-pop ${moreOpen ? 'open' : ''}`}>
                  {moreNav.map(renderPill)}
                </div>

                <button
                  type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  aria-expanded={moreOpen}
                  title={!expanded ? 'More' : undefined}
                  className={[
                    'edem-pill',
                    'edem-more-btn',
                    moreOpen ? 'edem-more-btn--on' : '',
                    !expanded ? 'edem-pill--icon' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <MoreVertical />
                  {expanded && <span className="edem-lbl">More</span>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
